import { debuglog } from './debug';
import { isHttpRequestError } from './errors';
import type { FailedAttempt } from './retry';
import type { SerializedBody, Serializer } from './types';

/**
 * Every line this library writes to the debug window, in one file.
 *
 * The six namespaces are named after the OPTION each one belongs to, so a
 * caller who knows `retry` knows to ask for `http-request:retry`. What each one
 * writes is decided here rather than at the call sites, which keeps the call
 * sites to a single named line and keeps the six formats readable side by side.
 *
 * TWO RULES SHAPE THE WRITERS BELOW.
 *
 * The channel is asked for at the moment of writing rather than captured at
 * module load, so a process that switches `NODE_DEBUG` on halfway through gets
 * lines from that moment. `debuglog` memoises on the spec, so the repeat ask is
 * a string comparison and a map lookup.
 *
 * `startCallLog` is the one exception, and it is deliberate: it asks once, where
 * the call starts, and hands back a closure that holds that answer for the whole
 * call. So a window opened DURING a call shows that call's retries but not its
 * closing line -- and the closing line is the one carrying the duration. The
 * next call is unaffected.
 *
 * And a call site never computes. Every writer takes values the caller already
 * has -- a status, a count, a url -- and anything that has to be derived from
 * them is derived INSIDE the guard, where an off namespace never reaches it.
 * That is why `logSerialize` takes the serializer rather than a word describing
 * it, and why these are fixed-arity functions rather than one variadic writer:
 * a rest parameter allocates its array at the call site, before anything has
 * looked at whether the namespace is on.
 */

/**
 * What a settled call reports where the upstream gave no status of its own.
 *
 * A failure that carries one still reports the status, because that is the same
 * fact by a different route -- a 404 is a 404 whether the caller asked for it as
 * a throw or as a result. Everything else names the CLASS, which is what
 * separates the cases that have no status at all: a timeout, an abort, a
 * network failure and a refused circuit all end a call without one, and the
 * class is the only thing that tells them apart.
 */
function outcomeOf(failure: unknown): number | string {
  if (isHttpRequestError(failure)) return failure.status;
  // DUCK-TYPED, for the reason `isHttpError` is and with the same measurement
  // behind it: a value built in another realm shares no prototype chain with
  // this one, so `instanceof Error` answers `false` for something that plainly
  // IS an error. This branch exists for a throw from the CALLER's own code,
  // which is a value from somewhere else by definition, so `instanceof` was
  // wrong here more often than it was right (DB17).
  //
  // Guarded, because a log line may not be what ends a call: a throwing getter
  // or a `Proxy` trap on `name` propagates out of a bare read, and this one runs
  // in the `finally` every exit passes through.
  try {
    if (failure !== null && typeof failure === 'object' && 'name' in failure) {
      const { name } = failure;
      if (typeof name === 'string') return name;
    }
  } catch {
    /* Falls through to the type, which is the honest answer about a value that
       will not say what it is. */
  }
  return typeof failure;
}

/** The one line a call writes when it settles: see `startCallLog`. */
export type CallLog = (
  method: string,
  url: string,
  status: number | undefined,
  failure: unknown,
) => void;

/** Built for a call that will write nothing, so it holds no clock. */
function noCallLog(): void {
  // Deliberately empty: logging is off, so settling writes nothing.
}

/**
 * The bare `http-request` namespace: one line where a call settles.
 *
 * ONE LINE PER CALL, not per attempt. The retries have their own namespace, so
 * this is built once at the top of `request` and fired once where it settles.
 * The duration therefore covers everything the caller waited for: the handlers,
 * the policies, every attempt and every wait between them.
 *
 * THE CLOCK IS THE CALL'S, handed in rather than started here. It is the same
 * `startedAt` that `durationMs` on a failure is measured from, and both lines
 * carry the same `callId` -- so an operator correlating on that id reads one
 * duration for one call rather than two that disagree.
 *
 * A call with debugging off pays the map lookup in `debuglog` and nothing else,
 * since the clock it uses is one the call was reading anyway.
 *
 * `status` and `failure` are both handed in because most settled calls have
 * either, and some have both: a 503 that ends the call is a status AND a
 * failure, and an upstream that answered 200 is neither an error nor an
 * absence.
 *
 * A call can have NEITHER a status nor a classified failure, and until
 * 2026-09-10 the line then read `undefined` for what went wrong. A throw from
 * the caller's own code -- a `before` handler, a policy -- ends a call that way,
 * and `outcome.error` deliberately holds only what this library produced.
 *
 * `failure` is now the RAW ending rather than the classified one, and that is no
 * claim about who produced it: `outcomeOf` reports a value by its own `name`, so
 * a `TypeError` reads as `TypeError`. MEASURED before the repair, on a `before`
 * handler that threw: `GET https://a.nl/thrown undefined 0`.
 */
export function startCallLog(callId: string, startedAt: number): CallLog {
  const log = debuglog('http-request');
  if (!log.enabled) return noCallLog;

  return (method, url, status, failure) => {
    log(
      '%s %s %s %s %dms',
      callId,
      method,
      url,
      status ?? outcomeOf(failure),
      Date.now() - startedAt,
    );
  };
}

/**
 * One line per SCHEDULED repeat -- which attempt of how many, why it is
 * repeating, and how long it waits first.
 *
 * Fired where the loop takes the wait rather than where it decides the failure
 * is retryable, so this counts decisions to repeat and not every retryable
 * failure. A wait that no longer fits the deadline ends the call instead, and
 * there is no repeat to report.
 *
 * A line is not a promise that the repeat goes out. The caller's own `signal` is
 * asked after the pause, so a caller who hangs up mid-attempt leaves a line here
 * with no attempt behind it.
 */
export function logRetry(failed: FailedAttempt, delayMs: number): void {
  const log = debuglog('http-request:retry');
  if (!log.enabled) return;
  log(
    'attempt %d/%d failed with %s, next in %dms',
    failed.attempt,
    failed.config.maxAttempts,
    outcomeOf(failed.failure),
    delayMs,
  );
}

/**
 * DB2, DB4: whether this caller opened a flight, joined one, or was answered
 * from `coalesce.cache` (`hit`), and on which key.
 *
 * The whole key, unshortened. It is what decided the answer, and a caller
 * looking at this window is looking at it precisely because two calls pooled
 * that should not have, or did not pool when they should -- which is a question
 * about the ingredients, and only the full key carries those.
 */
export function logCoalesce(
  kind: 'hit' | 'joined' | 'opened',
  key: string,
): void {
  const log = debuglog('http-request:coalesce');
  if (log.enabled) log('%s %s', kind, key);
}

/** DB2, DB4: which validator ran, and whether it let the call through. */
export function logValidate(
  direction: 'request' | 'response',
  passed: boolean,
): void {
  const log = debuglog('http-request:validate');
  if (log.enabled) log('%s validator %s', direction, passed ? 'ok' : 'failed');
}

/**
 * DB2, DB4: which transformation wrote the body, and the label it chose.
 *
 * The serializer itself rather than a word describing it, so the call site
 * decides nothing: which of the two ran is one comparison, and it happens
 * inside the guard.
 *
 * A body that is not there reports NOTHING, because nothing ran. Most traffic
 * is bodyless -- every `GET` is -- so a line per call would be one line of
 * "no body" for every line that means something, and the lines that mean
 * something are the reason to open this window at all.
 */
export function logSerialize(
  serializer: Serializer | undefined,
  body: SerializedBody | undefined,
  contentType: string | undefined,
): void {
  const log = debuglog('http-request:serialize');
  if (!log.enabled || body === undefined) return;
  log(
    '%s serialized body as %s',
    serializer === undefined ? 'built-in' : 'own',
    contentType ?? 'no content-type',
  );
}

/** DB2: the breaker moving between its three states. */
export function logBreakerState(from: string, to: string, url: string): void {
  const log = debuglog('http-request:policy');
  if (log.enabled) log('breaker %s -> %s on %s', from, to, url);
}

/** DB2: a call the throttle holds back, with how many are already waiting. */
export function logThrottleWait(waiting: number, url: string): void {
  const log = debuglog('http-request:policy');
  if (log.enabled) log('throttle: waiting behind %d for %s', waiting, url);
}

/** DB2: a call the bulkhead queues, with how deep the queue already is. */
export function logBulkheadQueued(queued: number, url: string): void {
  const log = debuglog('http-request:policy');
  if (log.enabled) log('bulkhead: queued behind %d for %s', queued, url);
}
